import { Stack, Typography } from '@mui/material';
import { NotificationImportant } from '@mui/icons-material';
import {
  EqiupmentCardFieldTypeEnum,
  EquipmentCardPositionEnum,
  EquipmentDetailTypeEnum,
  Maybe,
  OptionFieldTypeEnum,
  TemplateFieldTypeEnum,
} from '@/__generated__/graphql';
import {
  CriticalityLevelMapper,
  ScCategory,
} from '@/components/Analysis/shared/EquipmentsTab/common';
import { AlarmLevels } from '@/components/Analysis/shared/EquipmentsTab/common';
import { DetailText } from './styles';
import { InlineTypography } from '../../../shared/EquipmentsTab/EquipmentCard/styles';
import { titleCaseSentence } from '@/utils';
import { NOT_APPLICABLE_LABEL } from '@/constants';

type ComponentCardTemplateRendererProps = {
  data: {
    name: string | undefined;
    value: Maybe<string> | AlarmLevels | undefined;
    type:
      | TemplateFieldTypeEnum
      | OptionFieldTypeEnum
      | EqiupmentCardFieldTypeEnum
      | EquipmentDetailTypeEnum
      | undefined;
    position: EquipmentCardPositionEnum | undefined;
  };
  isHighlighted?: boolean;
};

export const ComponentCardTemplateRenderer = ({
  data,
  isHighlighted,
}: ComponentCardTemplateRendererProps) => {
  switch (data?.type) {
    case EqiupmentCardFieldTypeEnum.SimpleText: {
      return (
        <DetailText highlighted={isHighlighted} variant="caption">
          {titleCaseSentence(data.value ?? NOT_APPLICABLE_LABEL)}
        </DetailText>
      );
    }
    case EqiupmentCardFieldTypeEnum.AlarmLevel: {
      const criticalityLevelMapper =
        CriticalityLevelMapper[(data?.value as AlarmLevels) || AlarmLevels.CLEAN];
      return (
        <InlineTypography sx={{ fontSize: '1.4rem', fontWeight: 700 }}>
          <NotificationImportant fontSize="small" sx={{ color: criticalityLevelMapper?.color }} />
          {criticalityLevelMapper?.alarmLevel === AlarmLevels.CLEAN
            ? '-'
            : criticalityLevelMapper?.alarmLevel}
        </InlineTypography>
      );
    }
    case EqiupmentCardFieldTypeEnum.ScCategory: {
      const criticalityLevelMapper =
        CriticalityLevelMapper[(data?.value as ScCategory) || ScCategory.SC_0];
      return (
        <Stack direction="row" alignItems="center" gap="0.8rem">
          <NotificationImportant
            sx={{ color: criticalityLevelMapper?.color, fontSize: '1.6rem' }}
          />
          <Typography
            variant="h6"
            color={isHighlighted ? 'primary.100' : 'primary.500'}
            lineHeight="normal"
          >
            {criticalityLevelMapper?.category === '0'
              ? NOT_APPLICABLE_LABEL
              : criticalityLevelMapper?.category || NOT_APPLICABLE_LABEL}
          </Typography>
        </Stack>
      );
    }
    case EqiupmentCardFieldTypeEnum.DamageClass: {
      return (
        <DetailText highlighted={isHighlighted} variant="caption">
          {titleCaseSentence(data.value ?? NOT_APPLICABLE_LABEL)}
        </DetailText>
      );
    }
    case 'TEXT_WITH_TITLE': {
      return (
        <DetailText highlighted={isHighlighted} variant="caption">
          {data.name}: {titleCaseSentence(data.value ?? NOT_APPLICABLE_LABEL)}
        </DetailText>
      );
    }
    case EqiupmentCardFieldTypeEnum.PercentageIndicator: {
      return (
        <DetailText highlighted={isHighlighted} variant="caption">
          {data.name}: {titleCaseSentence(data.value ? `${data.value}%` : NOT_APPLICABLE_LABEL)}
        </DetailText>
      );
    }
    default:
      return <DetailText variant="caption">-</DetailText>;
  }
};
